/* eslint-disable quote-props */
/* eslint-disable @next/next/no-img-element */
import { imageSrc } from '@/lib/imageSrc';
import hljs from 'highlight.js/lib/core';
import css from 'highlight.js/lib/languages/css';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import xml from 'highlight.js/lib/languages/xml';
import { useEffect, useRef, useState } from 'react';

// Register commonly used languages
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('css', css);
hljs.registerLanguage('python', python);
hljs.registerLanguage('html', xml);

const EditorJsRenderer = ({ blocks }: { blocks: any[] }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    // Initialize syntax highlighting
    if (contentRef.current) {
      const codeBlocks = contentRef.current.querySelectorAll('pre code');
      codeBlocks.forEach((block) => {
        hljs.highlightElement(block as HTMLElement);
      });
    }
  }, [blocks]);

  // Helper function to safely render HTML content
  const renderHTML = (html: string) => {
    return { __html: html || '' }; // Handle null/undefined with empty string fallback
  };

  // Process marked text to apply proper styling
  const processMarkedText = (text: string) => {
    if (!text) return '';

    // Add proper styling for highlighted text with cdx-marker
    let processedText = text;

    // Handle highlighted text
    if (text.includes('<mark class="cdx-marker">')) {
      processedText = processedText.replace(
        /<mark class="cdx-marker">(.*?)<\/mark>/g,
        '<mark class="bg-yellow-200 dark:bg-yellow-900 px-1 rounded">$1</mark>',
      );
    }

    // Handle inline code
    if (text.includes('<code>')) {
      processedText = processedText.replace(
        /<code>(.*?)<\/code>/g,
        '<code class="bg-neutral-100 text-black dark:bg-neutral-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>',
      );
    }

    // Ensure links have proper styling
    if (text.includes('<a')) {
      processedText = processedText.replace(
        /<a(.*?)>(.*?)<\/a>/g,
        '<a$1 class="text-primary hover:underline">$2</a>',
      );
    }

    return processedText;
  };

  // Recursively render nested lists
  const renderNestedList = (items: any[], isOrdered: boolean) => {
    const ListTag = isOrdered ? 'ol' : 'ul';
    const listClass = isOrdered
      ? 'list-decimal pl-6 mb-2 space-y-1 mt-2'
      : 'list-disc pl-6 mb-2 space-y-1 mt-2';

    return (
      <ListTag className={listClass}>
        {items.map((item: any, itemIndex: number) => {
          // Handle simple string items
          if (typeof item === 'string') {
            return (
              <li
                key={itemIndex}
                className="pl-2"
                dangerouslySetInnerHTML={renderHTML(processMarkedText(item))}
              />
            );
          }

          // Handle object items with nested content
          if (item.content) {
            return (
              <li key={itemIndex} className="pl-2">
                <span
                  dangerouslySetInnerHTML={renderHTML(
                    processMarkedText(item.content),
                  )}
                />
                {item.items &&
                  item.items.length > 0 &&
                  renderNestedList(item.items, item.style === 'ordered')}
              </li>
            );
          }

          return null;
        })}
      </ListTag>
    );
  };

  // Process list items that potentially contain nested lists
  const processListItem = (item: string | any) => {
    // Handle object with nested lists
    if (typeof item === 'object' && item !== null) {
      return (
        <>
          <span
            dangerouslySetInnerHTML={renderHTML(
              processMarkedText(item.content || ''),
            )}
          />
          {item.items &&
            item.items.length > 0 &&
            renderNestedList(item.items, item.style === 'ordered')}
        </>
      );
    }

    // Handle string that might contain newlines indicating nested content
    if (typeof item === 'string' && item.includes('\n')) {
      const parts = item.split('\n');
      const mainText = parts[0];
      const nestedItems = parts
        .slice(1)
        .filter((part) => part.trim().length > 0);

      if (nestedItems.length > 0) {
        return (
          <>
            <span
              dangerouslySetInnerHTML={renderHTML(processMarkedText(mainText))}
            />
            <ul className="mt-2 list-disc space-y-1 pl-6">
              {nestedItems.map((nestedItem, i) => (
                <li
                  key={i}
                  className="pl-2"
                  dangerouslySetInnerHTML={renderHTML(
                    processMarkedText(nestedItem),
                  )}
                />
              ))}
            </ul>
          </>
        );
      }
    }

    // Simple string item
    return (
      <span dangerouslySetInnerHTML={renderHTML(processMarkedText(item))} />
    );
  };

  // Handle toggle state changes
  const handleToggle = (id: string) => {
    setToggleStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Check if blocks array exists and is not empty
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return <div className="p-4 text-neutral-500">No content to display</div>;
  }

  return (
    <div ref={contentRef} className="editor-js-content">
      {blocks.map((block, index) => {
        // Skip rendering if block is missing critical data
        if (!block || !block.type) {
          return null;
        }

        const blockId = block.id || `block-${index}`;

        switch (block.type) {
          case 'header': {
            const level = (block.data?.level as number) || 2; // Default to h2 if level is missing
            const HeaderTag = `h${level}` as keyof JSX.IntrinsicElements;
            const headerClasses =
              {
                1: 'text-4xl font-bold mt-8 mb-4',
                2: 'text-3xl font-bold mt-7 mb-3',
                3: 'text-2xl font-bold mt-6 mb-2',
                4: 'text-xl font-semibold mt-5 mb-2',
                5: 'text-lg font-semibold mt-4 mb-2',
                6: 'text-base font-semibold mt-3 mb-2',
              }[level as 1 | 2 | 3 | 4 | 5 | 6] ||
              'text-xl font-bold mt-4 mb-2';

            const text = block.data?.text || ''; // Handle empty text

            return (
              <HeaderTag
                key={blockId}
                className={headerClasses}
                id={`header-${blockId}`}
              >
                {text && (
                  <span
                    dangerouslySetInnerHTML={renderHTML(
                      processMarkedText(text),
                    )}
                  />
                )}
              </HeaderTag>
            );
          }

          case 'paragraph': {
            const alignment = block?.tunes?.textAlignment?.alignment || '';
            const alignClass =
              alignment === 'center'
                ? 'text-center'
                : alignment === 'right'
                  ? 'text-right'
                  : '';

            const text = block.data?.text || '';
            const processedText = processMarkedText(text);

            return (
              <p
                key={blockId}
                className={`mb-4 leading-relaxed ${alignClass}`}
                dangerouslySetInnerHTML={renderHTML(processedText)}
              />
            );
          }

          case 'list': {
            const isOrdered = block.data?.style === 'ordered';
            const ListTag = isOrdered ? 'ol' : 'ul';
            const listClass = isOrdered
              ? 'list-decimal pl-6 mb-6 space-y-2'
              : 'list-disc pl-6 mb-6 space-y-2';

            const items = block.data?.items || [];

            return (
              <ListTag key={blockId} className={listClass}>
                {items.map((item: string | any, itemIndex: number) => (
                  <li key={`${blockId}-item-${itemIndex}`} className="pl-2">
                    {processListItem(item)}
                  </li>
                ))}
              </ListTag>
            );
          }

          case 'nestedList': {
            // Special case for explicitly nested lists
            const items = block.data?.items || [];
            const style = block.data?.style || 'unordered';
            const isOrdered = style === 'ordered';

            return (
              <div key={blockId}>{renderNestedList(items, isOrdered)}</div>
            );
          }

          case 'image': {
            const { file, caption, withBorder, withBackground, stretched } =
              block.data || {};

            // Build classes based on image properties
            const figureClasses = [
              'my-6',
              withBackground
                ? 'bg-neutral-100 dark:bg-neutral-800 p-4 rounded-lg'
                : '',
              withBorder
                ? 'border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden'
                : '',
              stretched ? 'w-full' : 'mx-auto',
            ]
              .filter(Boolean)
              .join(' ');

            const imageClasses = [
              'mx-auto ',
              withBorder ? 'border bg-gray-50 ' : '',
              stretched ? 'w-full' : 'max-w-full h-auto',
            ]
              .filter(Boolean)
              .join(' ');

            // Handle both URL formats (string or object with url property)
            const imageUrl = typeof file === 'string' ? file : file?.url;

            return (
              <figure key={blockId} className={figureClasses}>
                {imageUrl ? (
                  <>
                    <img
                      src={imageSrc(imageUrl)}
                      alt={caption || 'Blog image'}
                      className={imageClasses}
                      loading="lazy"
                    />
                    {caption && (
                      <figcaption className="mt-2 text-center text-sm text-neutral-500 dark:text-neutral-400">
                        {caption}
                      </figcaption>
                    )}
                  </>
                ) : (
                  <div className="rounded-lg bg-neutral-100 p-4 text-center dark:bg-neutral-800">
                    <p className="text-red-500 dark:text-red-400">
                      Image not available
                    </p>
                  </div>
                )}
              </figure>
            );
          }

          case 'code': {
            const language = block.data?.language || 'language-javascript';
            const code = block.data?.code || '';
            const caption = block.data?.caption || '';

            return (
              <div key={blockId} className="my-6">
                {code && (
                  <pre className="language-javascript overflow-x-auto rounded-lg bg-neutral-100 p-4 text-gray-600 dark:bg-neutral-800 dark:text-gray-50">
                    <code className={language}>{code}</code>
                  </pre>
                )}
                {caption && (
                  <p className="mt-2 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    {caption}
                  </p>
                )}
              </div>
            );
          }

          case 'quote': {
            const text = block.data?.text || '';
            const caption = block.data?.caption || '';
            const alignment = block.data?.alignment || 'left';

            const alignmentClass =
              alignment === 'center'
                ? 'text-center'
                : alignment === 'right'
                  ? 'text-right'
                  : '';

            return (
              <blockquote
                key={blockId}
                className={`my-6 border-l-4 border-neutral-300 py-2 pl-4 italic dark:border-neutral-600 ${alignmentClass}`}
              >
                <p
                  className="mb-2"
                  dangerouslySetInnerHTML={renderHTML(processMarkedText(text))}
                />
                {caption && (
                  <cite className="block text-right text-sm not-italic text-neutral-500 dark:text-neutral-400">
                    — {caption}
                  </cite>
                )}
              </blockquote>
            );
          }

          case 'table': {
            const content = block.data?.content || [];
            const withHeadings = block.data?.withHeadings || false;

            return (
              <div key={blockId} className="my-6 overflow-x-auto">
                <table className="min-w-full border-collapse border border-neutral-200 dark:border-neutral-700">
                  <tbody>
                    {content.map((row: string[], rowIndex: number) => (
                      <tr
                        key={`${blockId}-row-${rowIndex}`}
                        className={
                          rowIndex === 0 && withHeadings
                            ? 'bg-neutral-100 dark:bg-neutral-800'
                            : rowIndex % 2 === 0
                              ? 'bg-white dark:bg-neutral-900'
                              : 'bg-neutral-50 dark:bg-neutral-800'
                        }
                      >
                        {row.map((cell: string, cellIndex: number) => {
                          const CellTag =
                            rowIndex === 0 && withHeadings ? 'th' : 'td';
                          return (
                            <CellTag
                              key={`${blockId}-cell-${rowIndex}-${cellIndex}`}
                              className="border border-neutral-200 px-4 py-2 text-sm dark:border-neutral-700"
                              dangerouslySetInnerHTML={renderHTML(
                                processMarkedText(cell),
                              )}
                            />
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          case 'embed': {
            const { embed, service, width, height, caption } = block.data || {};
            const aspectRatio =
              height && width ? (height / width) * 100 : 56.25; // Default to 16:9

            if (!embed) {
              return (
                <div
                  key={blockId}
                  className="my-6 rounded-lg bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-300"
                >
                  Missing embed URL
                </div>
              );
            }

            // Handle different service types
            // eslint-disable-next-line prefer-const
            let embedClasses = 'absolute top-0 left-0 w-full h-full rounded-lg';

            // For YouTube and Vimeo, add extra attributes
            const extraProps =
              service === 'youtube' || service === 'vimeo'
                ? {
                    allow:
                      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
                  }
                : {};

            return (
              <div key={blockId} className="my-6">
                <div
                  className="relative w-full"
                  style={{ paddingBottom: `${aspectRatio}%` }}
                >
                  <iframe
                    className={embedClasses}
                    src={embed}
                    title={caption || `${service || 'Embedded'} content`}
                    frameBorder="0"
                    allowFullScreen
                    loading="lazy"
                    {...extraProps}
                  />
                </div>
                {caption && (
                  <p className="mt-2 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    {caption}
                  </p>
                )}
              </div>
            );
          }

          case 'delimiter': {
            return (
              <div
                key={blockId}
                className="my-8 flex items-center justify-center"
              >
                <div className="text-2xl tracking-widest text-neutral-400 dark:text-neutral-500">
                  •••
                </div>
              </div>
            );
          }

          case 'checklist': {
            const items = block.data?.items || [];

            return (
              <div key={blockId} className="my-4">
                {items.map((item: any, itemIndex: number) => (
                  <div
                    key={`${blockId}-check-${itemIndex}`}
                    className="mb-2 flex items-start"
                  >
                    <div className="mr-2 mt-0.5 flex h-5 items-center justify-center">
                      <input
                        type="checkbox"
                        checked={item.checked}
                        readOnly
                        className="h-4 w-4 rounded border-neutral-300 text-primary focus:ring-primary dark:border-neutral-700"
                      />
                    </div>
                    <span
                      className={`${item.checked ? 'text-neutral-500 line-through dark:text-neutral-400' : ''}`}
                      dangerouslySetInnerHTML={renderHTML(
                        processMarkedText(item.text),
                      )}
                    />
                  </div>
                ))}
              </div>
            );
          }

          case 'warning': {
            const title = block.data?.title || '';
            const message = block.data?.message || '';

            return (
              <div
                key={blockId}
                className="my-6 rounded-r-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3 dark:bg-amber-900/20"
              >
                {title && (
                  <div className="font-bold text-amber-800 dark:text-amber-400">
                    {title}
                  </div>
                )}
                <div
                  className="text-amber-700 dark:text-amber-300"
                  dangerouslySetInnerHTML={renderHTML(
                    processMarkedText(message),
                  )}
                />
              </div>
            );
          }

          case 'alert': {
            const alertType =
              (block.data?.type as 'info' | 'success' | 'warning' | 'error') ||
              'info';
            const message = block.data?.message || '';

            const alertStyles =
              {
                info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-800 dark:text-blue-300',
                success:
                  'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-800 dark:text-green-300',
                warning:
                  'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-800 dark:text-amber-300',
                error:
                  'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-800 dark:text-red-300',
              }[alertType] ||
              'bg-neutral-50 dark:bg-neutral-800 border-neutral-500 text-neutral-800 dark:text-neutral-300';

            return (
              <div
                key={blockId}
                className={`my-6 rounded-r-lg border-l-4 px-4 py-3 ${alertStyles}`}
              >
                <div
                  dangerouslySetInnerHTML={renderHTML(
                    processMarkedText(message),
                  )}
                />
              </div>
            );
          }

          case 'raw': {
            const html = block.data?.html || '';

            return (
              <div
                key={blockId}
                className="my-6"
                dangerouslySetInnerHTML={renderHTML(html)}
              />
            );
          }

          case 'attaches': {
            const { file, title } = block.data || {};

            if (!file || !file.url) {
              return (
                <div
                  key={blockId}
                  className="my-4 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
                >
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Attachment not available
                  </p>
                </div>
              );
            }

            return (
              <div
                key={blockId}
                className="my-4 flex items-center rounded-lg border border-neutral-200 p-3 dark:border-neutral-700"
              >
                <div className="mr-3 text-neutral-500 dark:text-neutral-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                </div>
                <div className="flex-grow">
                  <p className="font-medium">
                    {title || file.name || 'Attachment'}
                  </p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {file.size ? `${Math.round(file.size / 1024)} KB` : ''}
                  </p>
                </div>
                <a
                  href={imageSrc(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md bg-primary px-3 py-1 text-sm text-white hover:bg-primary/90"
                >
                  Download
                </a>
              </div>
            );
          }

          case 'linkTool': {
            const { link, meta } = block.data || {};

            if (!link) {
              return (
                <div
                  key={blockId}
                  className="my-4 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
                >
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Link not available
                  </p>
                </div>
              );
            }

            return (
              <a
                key={blockId}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="my-4 block rounded-lg border border-neutral-200 p-4 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800/50"
              >
                <div className="flex items-start">
                  {meta?.image && (
                    <div className="mr-4 flex-shrink-0">
                      <img
                        src={meta.image}
                        alt={meta.title || 'Link preview'}
                        className="h-16 w-16 rounded object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-grow">
                    <h4 className="truncate text-base font-medium">
                      {meta?.title || link}
                    </h4>
                    {meta?.description && (
                      <p className="line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
                        {meta.description}
                      </p>
                    )}
                    <div className="mt-1 flex items-center text-xs text-neutral-500 dark:text-neutral-500">
                      {meta?.site_name && (
                        <span className="mr-2">{meta.site_name}</span>
                      )}
                      <span className="truncate">{link}</span>
                    </div>
                  </div>
                </div>
              </a>
            );
          }

          case 'columns': {
            const columns = block.data?.columns || [];

            return (
              <div
                key={blockId}
                className="my-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
              >
                {columns.map((column: any[], colIndex: number) => (
                  <div
                    key={`${blockId}-col-${colIndex}`}
                    className="flex flex-col"
                  >
                    {column.map((nestedBlock: any, blockIndex: number) => {
                      // Recursively render content in each column
                      // For simplicity, we'll only support basic block types in columns
                      const nestedBlockId = `${blockId}-col-${colIndex}-block-${blockIndex}`;

                      if (nestedBlock.type === 'paragraph') {
                        return (
                          <p
                            key={nestedBlockId}
                            className="mb-4"
                            dangerouslySetInnerHTML={renderHTML(
                              processMarkedText(nestedBlock.data?.text || ''),
                            )}
                          />
                        );
                      }

                      if (nestedBlock.type === 'header') {
                        const level = nestedBlock.data?.level || 3;
                        const HeaderTag =
                          `h${level}` as keyof JSX.IntrinsicElements;

                        return (
                          <HeaderTag
                            key={nestedBlockId}
                            className="mb-2 font-bold"
                            dangerouslySetInnerHTML={renderHTML(
                              processMarkedText(nestedBlock.data?.text || ''),
                            )}
                          />
                        );
                      }

                      if (nestedBlock.type === 'image') {
                        return (
                          <figure key={nestedBlockId} className="mb-4">
                            <img
                              src={imageSrc(nestedBlock.data?.file?.url || '')}
                              alt={nestedBlock.data?.caption || 'Column image'}
                              className="w-full rounded-lg"
                              loading="lazy"
                            />
                            {nestedBlock.data?.caption && (
                              <figcaption className="mt-1 text-center text-sm text-neutral-500">
                                {nestedBlock.data.caption}
                              </figcaption>
                            )}
                          </figure>
                        );
                      }

                      return (
                        <div
                          key={nestedBlockId}
                          className="mb-4 text-sm text-neutral-500"
                        >
                          Unsupported block type in column: {nestedBlock.type}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          }

          case 'personality': {
            const { name, description, link, photo } = block.data || {};

            return (
              <div
                key={blockId}
                className="my-6 flex items-center rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800/50"
              >
                {photo && (
                  <div className="mr-4 flex-shrink-0">
                    <img
                      src={imageSrc(photo)}
                      alt={name || 'Person'}
                      className="h-16 w-16 rounded-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div>
                  {name && <h4 className="text-lg font-medium">{name}</h4>}
                  {description && (
                    <p
                      className="text-sm text-neutral-600 dark:text-neutral-400"
                      dangerouslySetInnerHTML={renderHTML(
                        processMarkedText(description),
                      )}
                    />
                  )}
                  {link && (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-sm text-primary hover:underline"
                    >
                      Website
                    </a>
                  )}
                </div>
              </div>
            );
          }

          case 'button': {
            const { text, url, alignment } = block.data || {};

            const alignmentClass =
              alignment === 'center'
                ? 'text-center'
                : alignment === 'right'
                  ? 'text-right'
                  : '';

            return (
              <div key={blockId} className={`my-6 ${alignmentClass}`}>
                <a
                  href={url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block rounded-md bg-primary px-6 py-2 text-white transition-colors hover:bg-primary/90"
                >
                  {text || 'Click here'}
                </a>
              </div>
            );
          }

          case 'toggle': {
            const { title, content } = block.data || {};
            const isOpen = toggleStates[blockId] || false;

            return (
              <details
                key={blockId}
                className="my-6 overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-700"
                open={isOpen}
              >
                <summary
                  className="flex cursor-pointer items-center justify-between bg-neutral-50 p-4 dark:bg-neutral-800"
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggle(blockId);
                  }}
                >
                  <span className="font-medium">{title || 'Toggle'}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div
                  className={`prose max-w-none p-4 transition-all dark:prose-invert ${isOpen ? 'block' : 'hidden'}`}
                  dangerouslySetInnerHTML={renderHTML(
                    processMarkedText(content || ''),
                  )}
                />
              </details>
            );
          }

          case 'codeBox': {
            const { language, theme, code, caption } = block.data || {};

            const themeClass =
              theme === 'dark'
                ? 'bg-neutral-800 text-white'
                : 'bg-neutral-100 text-neutral-900';

            return (
              <div key={blockId} className="my-6">
                <div
                  className={`flex items-center justify-between rounded-t-lg p-2 ${themeClass}`}
                >
                  <span className="font-mono text-sm">
                    {language || 'code'}
                  </span>
                  <button
                    className="rounded px-2 py-1 text-sm transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    onClick={() => {
                      navigator.clipboard.writeText(code);
                      // Optionally show a toast notification
                      alert('Code copied to clipboard!');
                    }}
                  >
                    Copy
                  </button>
                </div>
                <pre
                  className={`overflow-x-auto rounded-b-lg p-4 ${themeClass}`}
                >
                  <code className={language ? `language-${language}` : ''}>
                    {code || ''}
                  </code>
                </pre>
                {caption && (
                  <p className="mt-2 text-center text-sm text-neutral-500 dark:text-neutral-400">
                    {caption}
                  </p>
                )}
              </div>
            );
          }

          // For any unsupported block type
          default:
            return (
              <div
                key={block.id || index}
                className="my-4 rounded border border-dashed border-neutral-300 p-3 dark:border-neutral-700"
              >
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Unsupported block type: {block.type}
                </p>
              </div>
            );
        }
      })}
    </div>
  );
};

export default EditorJsRenderer;
